// MobileDashboardPage — Iter61af.
// Faithful implementation of the user-provided HTML redesign
// (asa_accueil_redesign_v3.html) for the mobile /dashboard route.
// Design language: dark premium (#0B1330) + electric blue (#3E7BFA),
// iOS large title, scroll-snap pager for 3 pages (Tâches / Objectifs / Événements).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { localToday } from '../lib/dates';
import { NotifBell } from '../components/AppLayout';
import { PWAInstallButton } from '../components/PWAInstallButton';
import { ElevateInfluenceCard } from '../components/ElevateInfluenceCard';

const T = {
  bg: '#0B1330',
  surface: '#141D42',
  surface2: '#1C2650',
  hairline: 'rgba(255,255,255,0.09)',
  hairlineSoft: 'rgba(255,255,255,0.05)',
  text: '#F5F7FB',
  textMuted: '#8A93B5',
  textFaint: '#565F84',
  blue: '#3E7BFA',
  blueLight: '#7FADFF',
  blueDeep: '#2A56D6',
  danger: '#F0616B',
};

const CAT_COLORS = {
  montage: T.blueLight,
  derushage: '#FFC46B',
  developpement: '#8FE3B0',
  correction_monteur: '#FF9F7F',
  correction_montage: '#FF9F7F',
  publication: '#B58BFF',
};

const FONT = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Inter', sans-serif";

export default function MobileDashboardPage() {
  const { user, isFounder, isFounderStrict } = useAuth();
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [events, setEvents] = useState([]);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [calendlyEvents, setCalendlyEvents] = useState([]);
  const [calendlyConnected, setCalendlyConnected] = useState(false);
  const [calendlyLoading, setCalendlyLoading] = useState(true);
  const [page, setPage] = useState(0);
  const pagerRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get('/tasks').then((r) => r.data).catch(() => []),
      api.get('/objectives').then((r) => r.data).catch(() => []),
      api.get('/messages/conversations').then((r) => r.data).catch(() => []),
    ]).then(([t, o, c]) => {
      setTasks(t);
      setObjectives(o);
      // Events = tasks whose event_type is not a plain "task" (meetings, calls, deadlines, reviews)
      setEvents((t || []).filter((x) => x.event_type && x.event_type !== 'task'));
      setUnreadMsgs(c.reduce((s, cv) => s + (cv.unread_count || 0), 0));
    });
    // Iter 62cj — Calendly events for EVERY user (personal OAuth or founder PAT fallback)
    api.get('/calendly/events?days_ahead=14')
      .then((r) => {
        setCalendlyConnected(!!r.data?.connected);
        setCalendlyEvents(r.data?.events || []);
        // Iter 62fi — Si Calendly non connecté, démarre direct sur "Tâches du jour" (page 1)
        if (!r.data?.connected && pagerRef.current) {
          const w = pagerRef.current.offsetWidth || 0;
          if (w > 0) {
            pagerRef.current.scrollLeft = w;
            setPage(1);
          }
        }
      })
      .catch(() => { /* silent */ })
      .finally(() => setCalendlyLoading(false));
  }, []);

  const today = localToday();
  // Iter 62bt — Fix : sort by due_date ASC (soonest first) BEFORE slicing so
  // today's dated tasks always appear before undated ones. Also exclude sub-tasks
  // and bump limit to 12 so multiple project tasks (ex. Nash) don't get cut off.
  const myTodayTasks = useMemo(() => tasks.filter((t) =>
    (t.assigned_to === user?.id
      || t.assigned_to_derush === user?.id
      || t.assigned_to_montage === user?.id
      || (t.assignees || []).includes(user?.id))
    && !t.parent_task_id
    && t.stage !== 'done'
    && (!t.event_type || t.event_type === 'task')
    && (t.due_date === today || !t.due_date)
  ).sort((a, b) => {
    // Dated today first, then undated. Tie: earliest end_time then created_at.
    const aDated = a.due_date === today ? 0 : 1;
    const bDated = b.due_date === today ? 0 : 1;
    if (aDated !== bDated) return aDated - bDated;
    return (a.end_time || '99:99').localeCompare(b.end_time || '99:99');
  }).slice(0, 12), [tasks, user?.id, today]);

  const myGoals = useMemo(() => objectives.slice(0, 3), [objectives]);
  const todayEvents = useMemo(() => events.filter((e) => (e.due_date || '').startsWith(today)).slice(0, 3), [events, today]);

  const hour = new Date().getHours();
  const greeting = hour < 6 ? 'Toujours debout' : hour < 12 ? 'Bonjour' : hour < 18 ? 'Bel après-midi' : 'Bonsoir';
  const dateLabel = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  const onScroll = () => {
    if (!pagerRef.current) return;
    const w = pagerRef.current.offsetWidth;
    const p = Math.round(pagerRef.current.scrollLeft / w);
    if (p !== page) setPage(p);
  };
  const gotoPage = (i) => {
    const w = pagerRef.current?.offsetWidth || 0;
    pagerRef.current?.scrollTo({ left: w * i, behavior: 'smooth' });
  };

  return (
    <div
      data-testid="mobile-dashboard"
      style={{
        // Iter61g — locked height, no vertical scroll allowed. Only horizontal swipe on pager.
        height: '100dvh', maxHeight: '100dvh',
        background: T.bg, color: T.text, fontFamily: FONT,
        WebkitTapHighlightColor: 'transparent',
        paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + 78px)`,
        position: 'relative', overflow: 'hidden',
        overscrollBehavior: 'none',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Ambient glows */}
      <div style={{
        position: 'absolute', top: -180, left: -120, width: 460, height: 460,
        background: `radial-gradient(circle, ${T.blue}33 0%, transparent 60%)`,
        filter: 'blur(24px)', pointerEvents: 'none', zIndex: 0,
      }} />
      <div style={{
        position: 'absolute', top: 40, right: -140, width: 380, height: 380,
        background: `radial-gradient(circle, ${T.blueDeep}55 0%, transparent 65%)`,
        filter: 'blur(20px)', pointerEvents: 'none', zIndex: 0,
      }} />

      {/* Topbar — Iter61c: NotifBell replaces decorative bell to have a real popover panel.
          Iter61d: zIndex bumped to 70 so the popover renders above the pager, dots, ambient glows and bottom-nav.
          Iter62ao: PWA install pill moved here (topbar-inline) for founders so it
          doesn't eat vertical space in the pager. */}
      <div style={{
        position: 'relative', zIndex: 70,
        padding: `calc(env(safe-area-inset-top, 0px) + 12px) 22px 8px`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10,
      }}>
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
          color: T.textMuted, textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>Elevate Influence</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isFounder && (
            <div data-testid="mobile-dashboard-pwa-slot" style={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
              <PWAInstallButton label="Widget iPhone" target="/widget/calendar" />
            </div>
          )}
          <div data-testid="mobile-dashboard-bell">
            <NotifBell placement="ios" />
          </div>
        </div>
      </div>

      {/* Large title */}
      <div style={{ position: 'relative', zIndex: 2, padding: '10px 26px 22px' }}>
        <div style={{
          fontSize: 13, color: T.textMuted, fontWeight: 500, letterSpacing: '0.02em',
          textTransform: 'capitalize', marginBottom: 4,
        }}>{dateLabel}</div>
        <div style={{
          fontSize: 32, fontWeight: 700, lineHeight: 1.1,
          color: T.text, letterSpacing: '-0.02em',
        }}>
          {greeting},{' '}
          <span style={{
            background: `linear-gradient(135deg, ${T.blueLight}, ${T.blueDeep})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>{user?.first_name || (user?.name || '').split(' ')[0] || 'toi'}</span>
        </div>
      </div>

      {/* Pager — Iter61g: takes remaining vertical space, no vertical overflow */}
      <div
        ref={pagerRef}
        onScroll={onScroll}
        style={{
          position: 'relative', zIndex: 2,
          flex: 1, minHeight: 0,
          display: 'flex', overflowX: 'auto', overflowY: 'hidden',
          scrollSnapType: 'x mandatory', scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          touchAction: 'pan-x',
        }}
        className="asa-hide-scrollbar"
      >
        {/* Iter 62cj — RDV Calendly first (every user with OAuth + founder fallback) */}
        <PagerPage>
          <PageTitle>RDV Calendly</PageTitle>
          <CalendlyMobilePage
            loading={calendlyLoading}
            connected={calendlyConnected}
            events={calendlyEvents}
            onConnect={() => nav('/reglages')}
          />
        </PagerPage>
        <PagerPage>
          <PageTitle>Tâches du jour</PageTitle>
          <TaskListCard tasks={myTodayTasks} onOpen={(t) => nav(`/tasks?open=${t.id}`)} onAdd={() => nav('/tasks')} />
          {unreadMsgs > 0 && (
            <MessageBanner count={unreadMsgs} onClick={() => nav('/messages')} />
          )}
        </PagerPage>
        <PagerPage>
          <PageTitle>Objectifs de la semaine</PageTitle>
          {myGoals.length === 0 ? (
            <Empty>Pas d’objectif cette semaine</Empty>
          ) : myGoals.map((g) => (
            <GoalCard key={g.id} goal={g} onClick={() => nav(`/objectives?open=${g.id}`)} />
          ))}
        </PagerPage>
        <PagerPage>
          <PageTitle>Événements</PageTitle>
          {todayEvents.length === 0 ? (
            <Empty>Aucun événement aujourd’hui</Empty>
          ) : todayEvents.map((e) => (
            <EventCard key={e.id} ev={e} onClick={() => nav(`/planning?event=${e.id}`)} />
          ))}
        </PagerPage>
        {/* Iter 62bp — Elevate Influence page hidden for gestionnaires */}
        {isFounderStrict && (
          <PagerPage>
            <PageTitle>Elevate Influence</PageTitle>
            <ElevateInfluenceCard onOpenAll={() => nav('/planning')} />
          </PagerPage>
        )}
      </div>

      {/* Pagination dots — iOS native: tiny 5px dots. Use <span> to bypass global button { min-height: 36px }. */}
      <div style={{
        position: 'relative', zIndex: 2,
        display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center',
        padding: '14px 0 4px',
      }}>
        {(isFounderStrict ? [0, 1, 2, 3, 4] : [0, 1, 2, 3]).map((i) => (
          <span
            key={i}
            role="button"
            tabIndex={0}
            onClick={() => gotoPage(i)}
            data-testid={`mobile-dash-dot-${i}`}
            aria-label={`Aller à la page ${i + 1}`}
            style={{
              display: 'block',
              width: 5, height: 5, borderRadius: '50%',
              background: page === i ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.22)',
              cursor: 'pointer', transition: 'background 0.2s ease',
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function PagerPage({ children }) {
  return (
    <div style={{
      flex: '0 0 100%', minWidth: '100%',
      scrollSnapAlign: 'center', padding: '0 22px',
      // Iter61j — center content vertically in the available space
      height: '100%', overflowY: 'auto', overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
    }} className="asa-hide-scrollbar">{children}</div>
  );
}

function PageTitle({ children }) {
  return (
    <div style={{
      fontSize: 13, fontWeight: 700, color: T.textMuted,
      letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 12,
    }}>{children}</div>
  );
}

function TaskListCard({ tasks, onOpen, onAdd }) {
  return (
    <div
      data-testid="mobile-dash-task-list"
      style={{
        background: `linear-gradient(160deg, #1B2856, #101A3C)`,
        borderRadius: 20, padding: '4px 4px',
        border: `1px solid ${T.hairline}`,
        boxShadow: '0 10px 28px -12px rgba(0,0,0,0.5)',
      }}
    >
      {tasks.length === 0 ? (
        <div style={{ padding: '18px 16px', color: T.textMuted, fontSize: 13 }}>
          Rien à faire aujourd’hui 🎉
        </div>
      ) : tasks.map((t, i) => (
        <TaskRow key={t.id} task={t} last={i === tasks.length - 1} onClick={() => onOpen(t)} />
      ))}
      <button
        data-testid="mobile-dash-add-task"
        onClick={onAdd}
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '13px 14px',
          background: 'transparent', border: 'none', cursor: 'pointer',
          fontFamily: FONT,
        }}
      >
        <div style={{
          width: 22, height: 22, borderRadius: '50%',
          border: `1.5px dashed ${T.textFaint}`,
          display: 'grid', placeItems: 'center',
          color: T.textFaint, fontSize: 14, fontWeight: 400,
        }}>+</div>
        <span style={{
          flex: 1, textAlign: 'left', color: T.textMuted,
          fontSize: 15, fontWeight: 500,
        }}>Ajouter une tâche</span>
      </button>
    </div>
  );
}

function TaskRow({ task, last, onClick }) {
  const color = CAT_COLORS[task.category_tag] || T.blueLight;
  const timeLabel = task.due_date === localToday() ? `${(task.start_time || '').slice(0, 5) || 'Auj.'}` : (task.due_date ? new Date(task.due_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '');
  const urgent = task.due_date && task.due_date < localToday();
  return (
    <button
      onClick={onClick}
      data-testid={`mobile-dash-task-${task.id}`}
      style={{
        width: '100%',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '13px 14px',
        background: 'transparent',
        border: 'none',
        borderBottom: last ? 'none' : `1px solid ${T.hairlineSoft}`,
        cursor: 'pointer', fontFamily: FONT,
      }}
    >
      <div style={{
        width: 22, height: 22, borderRadius: '50%',
        background: `linear-gradient(135deg, ${color}, ${T.blueDeep})`,
        padding: 2,
      }}>
        <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: T.surface }} />
      </div>
      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <div style={{
          fontSize: 15.5, fontWeight: 600, color: T.text, lineHeight: 1.25,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{task.title}</div>
        <div style={{
          fontSize: 11.5, color: T.textMuted, textTransform: 'uppercase',
          letterSpacing: '0.08em', marginTop: 3, fontWeight: 600,
        }}>{task.category_tag ? task.category_tag.replace(/_/g, ' ') : 'Général'}</div>
      </div>
      {timeLabel && (
        <span style={{
          fontSize: 13, fontWeight: 700,
          color: urgent ? T.danger : T.textMuted,
          fontVariantNumeric: 'tabular-nums',
        }}>{timeLabel}</span>
      )}
    </button>
  );
}

function MessageBanner({ count, onClick }) {
  return (
    <button
      data-testid="mobile-dash-msg-banner"
      onClick={onClick}
      style={{
        marginTop: 14, width: '100%',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '13px 14px', borderRadius: 16,
        background: `rgba(62,123,250,0.10)`,
        border: `1px solid rgba(62,123,250,0.25)`,
        cursor: 'pointer', fontFamily: FONT,
      }}
    >
      <div style={{
        width: 34, height: 34, borderRadius: 10,
        background: `linear-gradient(135deg, ${T.blueLight}, ${T.blueDeep})`,
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
          {count} message{count > 1 ? 's' : ''} non lu{count > 1 ? 's' : ''}
        </div>
        <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>
          Touche pour ouvrir la messagerie
        </div>
      </div>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}

function GoalCard({ goal, onClick }) {
  const steps = goal.steps || [];
  const done = steps.filter((s) => s.done).length;
  const total = Math.max(steps.length, 1);
  const pct = Math.min(100, Math.round((done / total) * 100));
  return (
    <button
      data-testid={`mobile-dash-goal-${goal.id}`}
      onClick={onClick}
      style={{
        marginBottom: 12, padding: '16px 16px',
        background: `linear-gradient(160deg, #1B2856, #101A3C)`,
        borderRadius: 18, border: `1px solid ${T.hairline}`,
        width: '100%', textAlign: 'left', cursor: 'pointer',
        fontFamily: FONT,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{
          flex: 1, minWidth: 0,
          fontSize: 15, fontWeight: 600, color: T.text, lineHeight: 1.3,
        }}>{goal.text}</div>
        <span style={{
          fontSize: 12, fontWeight: 700, color: T.blueLight,
          fontVariantNumeric: 'tabular-nums',
        }}>{done}/{total}</span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: T.surface2, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: `linear-gradient(90deg, ${T.blueLight}, ${T.blueDeep})`,
          borderRadius: 999, transition: 'width 0.4s',
        }} />
      </div>
      {goal.owner && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            background: `linear-gradient(135deg, ${T.blueLight}, ${T.blueDeep})`,
            display: 'grid', placeItems: 'center',
            fontSize: 10, fontWeight: 700, color: '#fff',
          }}>{(goal.owner || '').slice(0, 1).toUpperCase()}</div>
          <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 500 }}>
            {goal.owner}
          </span>
        </div>
      )}
    </button>
  );
}

function EventCard({ ev, onClick }) {
  const time = ev.start_time || ev.due_date?.slice(11, 16) || '';
  const [h, m] = time.split(':');
  return (
    <button
      data-testid={`mobile-dash-event-${ev.id}`}
      onClick={onClick}
      style={{
        marginBottom: 10, padding: '14px 14px',
        background: `linear-gradient(160deg, #1B2856, #101A3C)`,
        borderRadius: 18, border: `1px solid ${T.hairline}`,
        display: 'flex', alignItems: 'center', gap: 14,
        width: '100%', textAlign: 'left', cursor: 'pointer',
        fontFamily: FONT,
      }}
    >
      <div style={{ textAlign: 'right', minWidth: 46 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.text, fontVariantNumeric: 'tabular-nums' }}>{h || '--'}</div>
        <div style={{ fontSize: 10.5, color: T.textMuted, fontWeight: 600, marginTop: 1 }}>{m || '--'}</div>
      </div>
      <div style={{
        width: 3, alignSelf: 'stretch', borderRadius: 999,
        background: `linear-gradient(180deg, ${T.blueLight}, ${T.blueDeep})`,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: T.text, lineHeight: 1.3 }}>{ev.title || 'Événement'}</div>
        {ev.description && (
          <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 3 }}>{ev.description}</div>
        )}
      </div>
    </button>
  );
}

function Empty({ children }) {
  return (
    <div style={{
      padding: '30px 16px', textAlign: 'center',
      color: T.textFaint, fontSize: 12.5,
      background: T.surface, borderRadius: 18,
      border: `1px dashed ${T.hairline}`,
    }}>{children}</div>
  );
}


// ---------------------------------------------------------------------------
// Iter 62cj — Calendly page (mobile dashboard first slide)
// ---------------------------------------------------------------------------
function CalendlyMobilePage({ loading, connected, events, onConnect }) {
  if (loading) {
    return (
      <div style={{
        padding: '30px 16px', textAlign: 'center',
        color: T.textFaint, fontSize: 12.5,
      }}>Chargement des RDV…</div>
    );
  }
  if (!connected) {
    return (
      <div
        data-testid="calendly-mobile-empty"
        style={{
          padding: '28px 20px', textAlign: 'center',
          background: T.surface, borderRadius: 22,
          border: `1px solid ${T.hairline}`,
        }}
      >
        <div style={{
          width: 48, height: 48, borderRadius: 14, margin: '0 auto 14px',
          background: 'linear-gradient(140deg, #F5C56A 0%, rgba(255,255,255,0.15) 50%, #F5C56A 100%)',
          display: 'grid', placeItems: 'center',
          boxShadow: '0 4px 14px rgba(245,197,106,0.35)',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="18" height="18" rx="3" stroke="#0B1220" strokeWidth="2.2" />
            <path d="M8 2V6M16 2V6M3 10H21" stroke="#0B1220" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </div>
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 16, color: T.text, marginBottom: 6 }}>
          Connecte ton Calendly
        </div>
        <div style={{
          fontSize: 12.5, color: T.textMuted, lineHeight: 1.55,
          marginBottom: 18, maxWidth: 300, marginLeft: 'auto', marginRight: 'auto',
        }}>
          En 1 clic, tes prochains rendez-vous s&apos;affichent ici comme sur la vue de Rayan.
        </div>
        <button
          data-testid="calendly-mobile-connect-cta"
          onClick={onConnect}
          style={{
            padding: '12px 26px', borderRadius: 999,
            background: `linear-gradient(135deg, ${T.blueLight}, ${T.blueDeep})`,
            color: '#0B1330', border: 'none',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
            cursor: 'pointer', boxShadow: '0 6px 16px rgba(62,123,250,0.35)',
          }}
        >
          Se connecter avec Calendly
        </button>
      </div>
    );
  }
  if (events.length === 0) {
    return <Empty>Aucun RDV Calendly prévu prochainement</Empty>;
  }
  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
      + ' · ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {events.slice(0, 6).map((e) => (
        <div
          key={e.uri}
          data-testid="calendly-mobile-event"
          style={{
            padding: '14px 16px',
            background: T.surface,
            border: `1px solid ${T.hairline}`,
            borderRadius: 16,
            display: 'flex', flexDirection: 'column', gap: 4,
          }}
        >
          <div style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: 14,
            color: T.text, letterSpacing: '-0.01em',
          }}>{e.name || 'RDV Calendly'}</div>
          <div style={{ fontSize: 12, color: T.textMuted }}>
            {fmtDate(e.start_time)}
          </div>
          {e.invitee_name && (
            <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>
              👤 {e.invitee_name}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
